import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ApolloClient, InMemoryCache, createHttpLink, ApolloProvider, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { store } from './store/store';
import { API_URL } from './config';
import { showErrorNotification } from './utils/errorHandler';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Operation: ${operation.operationName}`
      );
      
      // Check for authentication/authorization errors
      if (message.includes('UNAUTHENTICATED') || 
          message.includes('UNAUTHORIZED') || 
          message.includes('ACCOUNT_INACTIVE') ||
          message.includes('User not found or inactive') ||
          extensions?.code === 'UNAUTHENTICATED') {
        
        console.log('🚫 User authentication failed - logging out');
        
        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Show notification about account status
        if (message.includes('ACCOUNT_INACTIVE')) {
          alert('Your account has been deactivated. Please contact support.');
        } else {
          alert('Your session has expired. Please login again.');
        }
        
        // Redirect to login page
        window.location.href = '/login';
        return;
      }
    });
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`, operation.operationName);
    
    // Check for 401 Unauthorized responses
    if (networkError.statusCode === 401) {
      console.log('🚫 401 Unauthorized - logging out');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      alert('Your session has expired. Please login again.');
      window.location.href = '/login';
      return;
    }
    
    // Use the centralized error handler
    const error = { networkError, graphQLErrors };
    showErrorNotification(error);
  }
  
  if (graphQLErrors && graphQLErrors.length > 0) {
    const error = { graphQLErrors, networkError };
    showErrorNotification(error);
  }
});

// Create HTTP link
const httpLink = createHttpLink({
  uri: API_URL,
});

// Create auth link to add token to headers
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  console.log('Apollo Client - Adding token to request:', token ? 'Token exists' : 'No token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
});

// Create Apollo Client with error handling
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          contacts: {
            // Don't merge, always replace with fresh data
            keyArgs: ['search', 'isBlocked', 'isFavourite', 'groupId'],
            merge(existing, incoming) {
              return incoming;
            }
          },
          users: {
            // Always replace with fresh data
            merge(existing, incoming) {
              return incoming;
            }
          }
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only', // Always fetch from network
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only', // Always fetch from network
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

function App() {
  return (
    <ApolloProvider client={client}>
      <Provider store={store}>
        <ErrorBoundary>
          <Router>
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </Routes>
            </div>
          </Router>
        </ErrorBoundary>
      </Provider>
    </ApolloProvider>
  );
}

export default App;
